import React, { useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ViewStyle } from 'react-native';
import Head from 'expo-router/head';
import { ArrowLeft } from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  useTheme,
  SPACING,
  TYPOGRAPHY,
  BORDER_RADIUS,
  CONTENT_MAX_WIDTH,
  DESKTOP_CONTENT_MAX_WIDTH,
  SHELL_HEADER_HEIGHT,
  SHELL_RULE_WIDTH,
} from '../../theme';
import { Avatar } from '../common/Avatar';
import { TextLink } from '../common/TextLink';
import { useAuthStore } from '../../store/authStore';
import { useNotificationsStore } from '../../store/notificationsStore';
import { useUIStore } from '../../store/uiStore';
import { useDesktopShell } from '../../hooks/useDesktopShell';

interface HeaderProps {
  title?: string;
  subtitle?: string;
  showBack?: boolean;
  onBackPress?: () => void;
  /** Desktop shell only (standard, non-`large` header): a text label shown
   *  next to the back arrow ("Back"), turning it from a lone unlabeled icon
   *  into a named button. Ignored on phone/tablet, where the chevron alone
   *  stays the convention. */
  backLabel?: string;
  rightIcon?: LucideIcon;
  onRightPress?: () => void;
  rightLabel?: string;
  /** Optional second action rendered to the left of rightIcon (standard header only). */
  secondRightIcon?: LucideIcon;
  onSecondRightPress?: () => void;
  secondRightLabel?: string;
  transparent?: boolean;
  style?: ViewStyle;
  large?: boolean;
  /** Desktop shell only: align the header row to the wide content width
   * (1120px, for grids/tables) instead of the 768px reading column. */
  wide?: boolean;
  /** Desktop shell only: center the header on this exact width instead of
   * the `wide` binary — for a screen whose body uses a column width neither
   * preset matches (e.g. a News post's 680px reading column), so the title
   * lands on the same left edge as the content below it. Overrides `wide`
   * when both are passed (Sept 29 2026, F1). */
  contentMaxWidth?: number;
  /** Render the signed-in user's avatar on the right that opens the user menu.
   * Defaults to true when no rightIcon/rightLabel is provided and a user is signed in. */
  showAvatar?: boolean;
}

export function Header({
  title,
  subtitle,
  showBack = false,
  onBackPress,
  backLabel,
  rightIcon,
  onRightPress,
  rightLabel,
  secondRightIcon,
  onSecondRightPress,
  secondRightLabel,
  transparent = false,
  style,
  large = false,
  wide = false,
  contentMaxWidth,
  showAvatar,
}: HeaderProps) {
  const { colors } = useTheme();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const user = useAuthStore((s) => s.user);
  const openSheet = useUIStore((s) => s.openSheet);
  const isUserMenuOpen = useUIStore((s) => s.activeSheet === 'userMenu');
  const hasSeenTour = useUIStore((s) => s.hasSeenTour);
  // A new SW build is waiting — surface it as the same avatar dot used for
  // the tour nudge, since the avatar (opening the user menu -> Profile) is
  // this app's only Profile entry point on mobile. See ProfileFooter for the
  // tap-to-reload affordance.
  const swUpdateAvailable = useUIStore((s) => s.swUpdateAvailable);
  // Unread alerts light the avatar dot: the Alerts tab left the nav
  // (Aug 2026) and the user menu is now the only path to /alerts.
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const openUserMenu = useCallback(() => openSheet('userMenu'), [openSheet]);

  const isDesktopShell = useDesktopShell();

  const hasRightControl = Boolean(rightIcon || rightLabel || secondRightIcon);
  // On the desktop shell the sidebar footer is the single user entry point;
  // a second avatar in the content pane is duplicated chrome.
  const shouldShowAvatar =
    !isDesktopShell && (showAvatar ?? (!hasRightControl && Boolean(user))) && Boolean(user);
  const isAnon = user?.isAnonymous === true;
  const avatarName = isAnon
    ? ''
    : `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'User';

  const handleBack = () => {
    if (onBackPress) {
      onBackPress();
    } else {
      router.back();
    }
  };

  return (
    <View
      style={[
        styles.container,
        // Desktop shell: the header is a fixed band, not a padded one, so its
        // rule lands on the same y as the sidebar's divider. Pinning only the
        // top inset (what this did before) let the title's own line box decide
        // where the line fell, and the two were 6px apart.
        isDesktopShell && styles.containerDesktop,
        {
          paddingTop: isDesktopShell ? SPACING.lg : insets.top + SPACING.sm,
          backgroundColor: transparent ? 'transparent' : colors.background,
          borderBottomColor: transparent ? 'transparent' : colors.borderLight,
        },
        style,
      ]}>
      {title && (
        <Head>
          <title>{`${title} - Redi`}</title>
        </Head>
      )}
      <View
        style={
          isDesktopShell && [
            styles.desktopConstraint,
            {
              maxWidth: contentMaxWidth ?? (wide ? DESKTOP_CONTENT_MAX_WIDTH : CONTENT_MAX_WIDTH),
            },
          ]
        }>
        {large ? (
          /* Large header: title + avatar on one row */
          <>
            {/* The chevron takes its own 44px row on mobile. On the desktop
                shell that stacks two rows and pushes the header rule past
                SHELL_HEADER_HEIGHT, out of line with the sidebar's divider —
                so there it sits inline with the title instead, which is also
                less wasted band for a single icon. */}
            {showBack && !isDesktopShell && (
              <View style={styles.row}>
                <TouchableOpacity
                  onPress={handleBack}
                  style={styles.backButton}
                  accessibilityRole="button"
                  accessibilityLabel="Go back">
                  <ArrowLeft size={24} color={colors.text} />
                </TouchableOpacity>
              </View>
            )}
            {title && (
              <View style={styles.largeTitleRow}>
                {showBack && isDesktopShell && (
                  <TouchableOpacity
                    onPress={handleBack}
                    style={styles.backButtonInline}
                    accessibilityRole="button"
                    accessibilityLabel="Go back">
                    <ArrowLeft size={20} color={colors.text} />
                  </TouchableOpacity>
                )}
                <View style={styles.largeTitleContent}>
                  <Text
                    style={[
                      styles.largeTitle,
                      isDesktopShell && styles.largeTitleDesktop,
                      { color: colors.text },
                    ]}>
                    {title}
                  </Text>
                  {subtitle && (
                    <Text style={[styles.largeSubtitle, { color: colors.textSecondary }]}>
                      {subtitle}
                    </Text>
                  )}
                </View>
                {rightIcon && (
                  <TouchableOpacity
                    onPress={onRightPress}
                    style={styles.rightButton}
                    accessibilityRole="button"
                    accessibilityLabel={rightLabel ?? 'Action'}>
                    {React.createElement(rightIcon, { size: 24, color: colors.text })}
                  </TouchableOpacity>
                )}
                {rightLabel && !rightIcon && onRightPress && (
                  <TextLink
                    label={rightLabel}
                    onPress={onRightPress}
                    style={styles.rightLabelButton}
                    textStyle={styles.rightLabel}
                  />
                )}
                {!hasRightControl && shouldShowAvatar && (
                  <TouchableOpacity
                    onPress={openUserMenu}
                    style={[
                      styles.avatarButton,
                      {
                        borderColor: isUserMenuOpen ? colors.primary : 'transparent',
                        backgroundColor: isUserMenuOpen ? colors.primarySoft : 'transparent',
                      },
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel="Open user menu"
                    hitSlop={8}>
                    <Avatar
                      size={32}
                      name={avatarName}
                      imageUrl={isAnon ? null : user?.avatarUrl}
                      anonymous={isAnon}
                      showIndicator={
                        (!hasSeenTour || swUpdateAvailable || unreadCount > 0) && !isUserMenuOpen
                      }
                    />
                  </TouchableOpacity>
                )}
              </View>
            )}
          </>
        ) : (
          /* Standard header: left / center title / right on mobile; a single
           left-aligned row (back chevron, title, actions) on the desktop
           shell, where centered titles between fixed gutters read as a
           stretched phone app. */
          <View style={styles.row}>
            {(showBack || !isDesktopShell) && (
              <View style={[styles.leftContainer, isDesktopShell && styles.leftContainerDesktop]}>
                {showBack && (
                  <TouchableOpacity
                    onPress={handleBack}
                    style={[
                      styles.backButton,
                      isDesktopShell && backLabel && styles.backButtonLabeled,
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel={isDesktopShell && backLabel ? backLabel : 'Go back'}>
                    <ArrowLeft size={isDesktopShell ? 20 : 24} color={colors.text} />
                    {isDesktopShell && backLabel && (
                      <Text style={[styles.backLabel, { color: colors.text }]}>{backLabel}</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>
            )}

            {title ? (
              <View
                style={[styles.centerContainer, isDesktopShell && styles.centerContainerDesktop]}>
                <Text
                  style={[
                    styles.title,
                    isDesktopShell && styles.titleDesktop,
                    { color: colors.text },
                  ]}
                  numberOfLines={1}>
                  {title}
                </Text>
                {subtitle && (
                  <Text
                    style={[styles.subtitle, { color: colors.textSecondary }]}
                    numberOfLines={1}>
                    {subtitle}
                  </Text>
                )}
              </View>
            ) : (
              // Icons-only header (mockup dethead): without this spacer the
              // right actions sit beside the back chevron instead of docking
              // to the trailing edge.
              <View style={styles.centerSpacer} />
            )}

            <View style={[styles.rightContainer, isDesktopShell && styles.rightContainerDesktop]}>
              {secondRightIcon && (
                <TouchableOpacity
                  onPress={onSecondRightPress}
                  style={styles.rightButton}
                  accessibilityRole="button"
                  accessibilityLabel={secondRightLabel ?? 'Action'}>
                  {React.createElement(secondRightIcon, { size: 24, color: colors.text })}
                </TouchableOpacity>
              )}
              {rightIcon && (
                <TouchableOpacity
                  onPress={onRightPress}
                  style={styles.rightButton}
                  accessibilityRole="button"
                  accessibilityLabel={rightLabel ?? 'Action'}>
                  {React.createElement(rightIcon, { size: 24, color: colors.text })}
                </TouchableOpacity>
              )}
              {rightLabel && !rightIcon && onRightPress && (
                <TextLink
                  label={rightLabel}
                  onPress={onRightPress}
                  style={styles.rightLabelButton}
                  textStyle={styles.rightLabel}
                />
              )}
              {!hasRightControl && shouldShowAvatar && (
                <TouchableOpacity
                  onPress={openUserMenu}
                  style={[
                    styles.avatarButton,
                    {
                      borderColor: isUserMenuOpen ? colors.primary : 'transparent',
                      backgroundColor: isUserMenuOpen ? colors.primarySoft : 'transparent',
                    },
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel="Open user menu"
                  hitSlop={8}>
                  <Avatar
                    size={32}
                    name={avatarName}
                    imageUrl={isAnon ? null : user?.avatarUrl}
                    anonymous={isAnon}
                    showIndicator={
                      (!hasSeenTour || swUpdateAvailable || unreadCount > 0) && !isUserMenuOpen
                    }
                  />
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingBottom: SPACING.sm,
    overflow: 'hidden',
  },
  containerDesktop: {
    // Box height includes the border (RN is border-box), so the rule paints
    // the row at SHELL_HEADER_HEIGHT — where the sidebar's divider starts.
    //
    // minHeight, not height: a `large` header carrying a subtitle (the two
    // agent screens) needs about 95px, and a hard height would clip it
    // against the container's overflow:hidden. Every single-line header —
    // which is all of them bar those two — comes in under the floor and so
    // lands exactly on it.
    minHeight: SHELL_HEADER_HEIGHT + SHELL_RULE_WIDTH,
    // Matching the sidebar's 1px divider. hairlineWidth is 0.5px on a 2x
    // display, which read as a lighter grey right next to it.
    borderBottomWidth: SHELL_RULE_WIDTH,
  },
  // Desktop shell: header rows align to the same content column as the
  // screen body (ContentConstraint) instead of spanning the full pane.
  desktopConstraint: {
    width: '100%',
    alignSelf: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACING.md,
    minHeight: 44,
  },
  leftContainer: {
    width: 60,
    alignItems: 'flex-start',
    zIndex: 1,
  },
  leftContainerDesktop: {
    width: 'auto',
    marginRight: SPACING.xs,
  },
  centerContainer: {
    flex: 1,
    alignItems: 'center',
    zIndex: 1,
  },
  centerSpacer: {
    flex: 1,
  },
  centerContainerDesktop: {
    alignItems: 'flex-start',
  },
  largeTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.sm,
    paddingBottom: SPACING.xs,
  },
  largeTitleContent: {
    flex: 1,
  },
  rightContainer: {
    // minWidth (not width) so a second action icon can widen the slot; the
    // title stays visually centered for the common one-icon case.
    minWidth: 60,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: SPACING.xs,
    zIndex: 1,
  },
  rightContainerDesktop: {
    // No centering to balance on desktop, so the slot hugs its content.
    minWidth: 0,
  },
  backButton: {
    padding: SPACING.xs,
    marginLeft: -SPACING.xs,
  },
  // Desktop-shell standard header with a `backLabel`: icon + "Back" text
  // instead of a lone chevron. Same padding/marginLeft as backButton (the
  // left edge stays put), just laid out as a row with a gap for the label.
  backButtonLabeled: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  backLabel: {
    ...TYPOGRAPHY.label,
  },
  // Desktop-shell `large` header: chevron on the title's own row.
  backButtonInline: {
    padding: SPACING.xs,
    marginLeft: -SPACING.xs,
    marginRight: SPACING.xs,
  },
  rightButton: {
    padding: SPACING.xs,
    marginRight: -SPACING.xs,
  },
  rightLabelButton: {
    padding: SPACING.xs,
  },
  avatarButton: {
    marginRight: -SPACING.xs,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 2,
    borderColor: 'transparent',
    padding: 2,
  },
  rightLabel: {
    ...TYPOGRAPHY.button,
  },
  // One scale per role: standard header titles are h4 on every form factor.
  title: {
    ...TYPOGRAPHY.h4,
  },
  titleDesktop: {},
  subtitle: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: 2,
  },
  // Screen titles are the Plaud light register (28/300), the same preset the
  // feed and post-create screens set in their content. Bold h1 here was the
  // pre-Design-B hero and made Alerts/Search shout next to Observations.
  largeTitle: {
    ...TYPOGRAPHY.screenTitle,
  },
  // One scale on both form factors: screenTitle is already the shell-safe
  // size, so the desktop no longer needs its own step-down.
  largeTitleDesktop: {},
  largeSubtitle: {
    ...TYPOGRAPHY.body,
    marginTop: SPACING.xs,
  },
});
